import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, RefreshCw, ArrowLeft, CheckCircle2, AlertTriangle, PackageCheck, Truck, ChevronRight, CalendarDays } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { API } from '../../utils/kitchenTasks';
import { formatDateFR } from '../../utils/dateFormat';
import { formatApiError } from '../../services/api';
import ReceptionSwipeCard from './ReceptionSwipeCard';
import ReceptionDialog from './ReceptionDialog';

const STATUS_LABEL = { validee: 'Validée', finalized: 'Terminée', en_reception: 'En réception' };

// Réception des commandes par Swipe : liste des commandes à réceptionner → pile de cartes par ligne
export default function ReceptionPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [issue, setIssue] = useState(null); // {line, index}
  const [busy, setBusy] = useState(false);
  const [dateFilter, setDateFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/order-requests/reception/orders`);
      const list = r.data.data || [];
      setOrders(list);
      setCurrent(c => (c ? list.find(o => o.id === c.id) || null : null));
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pending = current ? current.items.map((l, i) => ({ ...l, _idx: i })).filter(l => !l.reception) : [];

  const send = async (index, received_quantity, comment) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/order-requests/orders/${current.id}/reception/lines/${index}`, { received_quantity, comment });
      const o = r.data.data;
      setCurrent(o);
      setOrders(prev => prev.map(x => (x.id === o.id ? o : x)).filter(x => x.status !== 'receptionnee'));
      setIssue(null);
      if (o.status === 'receptionnee') toast.success('Réception terminée !', { icon: <PackageCheck className="text-emerald-600" size={18} /> });
      else if (received_quantity === pending.find(l => l._idx === index)?.quantity) toast.success('Ligne conforme', { icon: <CheckCircle2 className="text-emerald-600" size={18} /> });
      else toast.warning('Écart enregistré', { icon: <AlertTriangle className="text-red-500" size={18} /> });
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  if (loading && !current) return <div className="flex justify-center py-12"><Loader2 className="w-10 h-10 text-neon-pink animate-spin" /></div>;

  if (!current) {
    const dates = [...new Set(orders.map(o => o.date))].sort();
    const shown = dateFilter ? orders.filter(o => o.date === dateFilter) : orders;
    return (
      <div className="max-w-3xl mx-auto" data-testid="reception-panel">
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-500 text-sm">Sélectionnez une commande livrée, puis glissez chaque ligne : à droite si conforme, à gauche en cas d'écart.</p>
          <Button variant="outline" onClick={load} className="h-11 rounded-full border-gray-200" data-testid="reception-refresh-btn"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /></Button>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="reception-date-filter">
          <div className="flex items-center gap-2 text-sm text-gray-700 font-medium"><CalendarDays size={16} className="text-gray-400" />Date de livraison</div>
          <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="h-11 px-3 rounded-xl bg-gray-100 border border-gray-200 text-gray-900 text-sm" data-testid="reception-date-input" />
          <div className="flex gap-1.5 overflow-x-auto">
            <button type="button" onClick={() => setDateFilter('')} className={`h-9 px-3 rounded-full text-xs font-medium border whitespace-nowrap ${!dateFilter ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`} data-testid="reception-date-all">Toutes ({orders.length})</button>
            {dates.map(d => <button key={d} type="button" onClick={() => setDateFilter(d)} className={`h-9 px-3 rounded-full text-xs font-medium border whitespace-nowrap ${dateFilter === d ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`} data-testid={`reception-date-${d}`}>{formatDateFR(d)}</button>)}
          </div>
        </div>
        {orders.length === 0 && <div className="border border-dashed border-gray-300 rounded-2xl p-10 text-center text-gray-500" data-testid="reception-empty"><Truck className="mx-auto mb-3 text-gray-300" size={36} />Aucune commande à réceptionner.<p className="text-xs text-gray-400 mt-2">Les commandes apparaissent ici dès que le chef a validé la commande du jour.</p></div>}
        {orders.length > 0 && shown.length === 0 && <div className="border border-dashed border-gray-300 rounded-2xl p-8 text-center text-gray-500" data-testid="reception-empty-date">Aucune commande à réceptionner pour le {formatDateFR(dateFilter)}.</div>}
        <div className="space-y-2">
          {shown.map(o => (
            <button key={o.id} type="button" onClick={() => setCurrent(o)} className="w-full text-left bg-white border border-gray-200 hover:border-neon-pink/50 rounded-2xl px-4 py-3 flex items-center gap-3 transition-colors" data-testid={`reception-order-${o.id}`}>
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0"><Truck size={20} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-gray-900">Livraison du {formatDateFR(o.date)}</span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">{STATUS_LABEL[o.status] || o.status}</span>
                  {o.items_with_issue > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-semibold border bg-red-50 text-red-700 border-red-200">{o.items_with_issue} écart{o.items_with_issue > 1 ? 's' : ''}</span>}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{o.items_received}/{o.items_total} lignes contrôlées · {o.kind === 'consolidated' ? 'commande consolidée' : 'commande directe'}{o.validated_by_name ? ` · validée par ${o.validated_by_name}` : ''}</p>
                <Progress value={o.items_total ? (o.items_received / o.items_total) * 100 : 0} className="h-1.5 mt-2 bg-gray-100" />
              </div>
              <ChevronRight className="text-gray-400 flex-shrink-0" size={20} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  const total = current.items.length;
  const done = total - pending.length;

  return (
    <div className="max-w-md mx-auto" data-testid="reception-swipe">
      <div className="flex items-center gap-2 mb-3">
        <Button variant="ghost" onClick={() => setCurrent(null)} className="h-10 rounded-full px-3" data-testid="reception-back-btn"><ArrowLeft size={18} className="mr-1" />Commandes</Button>
        <span className="text-sm text-gray-500 ml-auto" data-testid="reception-progress-text">{done}/{total} lignes</span>
      </div>
      <h2 className="font-heading text-xl font-bold text-gray-900">Livraison du {formatDateFR(current.date)}</h2>
      <Progress value={total ? (done / total) * 100 : 0} className="h-2 my-3 bg-gray-100" />

      <div className="relative" style={{ minHeight: '380px' }}>
        {pending.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center h-72 text-center" data-testid="reception-done">
            <div className="w-20 h-20 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mb-4"><PackageCheck className="text-emerald-600" size={40} /></div>
            <h3 className="font-heading text-2xl font-bold text-gray-900 mb-1">Réception terminée</h3>
            <p className="text-gray-500 mb-5">{current.items_with_issue ? `${current.items_with_issue} ligne(s) avec écart signalée(s) au chef.` : 'Toutes les lignes sont conformes.'}</p>
            <Button onClick={() => setCurrent(null)} className="rounded-full h-11 bg-neon-pink text-gray-900" data-testid="reception-finish-btn">Retour aux commandes</Button>
          </motion.div>
        ) : (
          <AnimatePresence mode="popLayout">
            {pending.slice(0, 3).reverse().map((l, i, arr) => (
              <ReceptionSwipeCard key={`${current.id}-${l._idx}`} line={l} index={l._idx} isTop={i === arr.length - 1}
                onSwipeRight={(line, idx) => send(idx, line.quantity, '')}
                onSwipeLeft={(line, idx) => setIssue({ line, index: idx })} />
            ))}
          </AnimatePresence>
        )}
      </div>

      {pending.length > 0 && (
        <div className="flex justify-center gap-6 mt-4">
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => setIssue({ line: pending[0], index: pending[0]._idx })} className="w-16 h-16 bg-white shadow-lg rounded-full flex items-center justify-center border-2 border-red-500/50" data-testid="reception-issue-btn" aria-label="Signaler un écart">
            <AlertTriangle className="text-red-600" size={28} />
          </motion.button>
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => send(pending[0]._idx, pending[0].quantity, '')} disabled={busy} className="w-16 h-16 bg-emerald-500 shadow-lg rounded-full flex items-center justify-center" data-testid="reception-ok-btn" aria-label="Conforme">
            {busy ? <Loader2 className="text-white animate-spin" size={28} /> : <CheckCircle2 className="text-white" size={32} />}
          </motion.button>
        </div>
      )}

      {done > 0 && (
        <div className="mt-6 space-y-1.5" data-testid="reception-checked-list">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Lignes contrôlées</h4>
          {current.items.filter(l => l.reception).map((l, i) => (
            <div key={`${l.product_id}-${i}`} className={`flex items-center justify-between text-sm rounded-xl px-3 py-2 border ${l.reception.status === 'conforme' ? 'bg-emerald-50/60 border-emerald-100' : 'bg-red-50/60 border-red-100'}`}>
              <span className="text-gray-800 truncate">{l.product_name}</span>
              <span className={`text-xs font-semibold ${l.reception.status === 'conforme' ? 'text-emerald-700' : 'text-red-700'}`}>{l.reception.received_quantity}/{l.quantity} {l.unit}{l.reception.comment ? ` · ${l.reception.comment}` : ''}</span>
            </div>
          ))}
        </div>
      )}

      <ReceptionDialog line={issue?.line} open={!!issue} onOpenChange={(v) => { if (!v) setIssue(null); }} busy={busy} onConfirm={(q, c) => send(issue.index, q, c)} />
    </div>
  );
}
